// Carga (frete) de uma viagem: contratante, rota, valor, pedágio,
// adiantamento e saldo, quem agenciou e, em "Mais detalhes", descontos,
// estadia, comprovante e o resto do Manifesto. Desde a 0.19.0: as entregas
// antes do destino final, o complemento (carga menor na mesma rota) e o botão
// "Carga entregue".
import { acertos, cargaEntregue, clientes, colaboradores, entregas, fretes, regras, veiculos, viagens } from '../dados.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerInteiro, lerNumero, numeroBR } from '../lib/formato.js';
import { principaisDaViagem, textoDaRota, textoDoMomento } from '../lib/entregas.js';
import { agenciamentoSugerido, liquidoDoFrete, regraDeAgenciamento, saldoDoFrete } from '../lib/fretes.js';
import { textoDosCamposDoMotorista } from '../lib/motorista.js';
import { UFS } from '../lib/validacao.js';
import { leitorDaChave } from '../ui/chave-do-cte.js';
import { nestaTela } from '../rotas.js';
import { contaGravavel, hojeDaEmpresa, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, confirmar, faixa, formulario, secao, vazio } from '../ui/componentes.js';
import { editorDeParadas } from '../ui/paradas.js';
import { faixaDaConta } from './inicio.js';

const dinheiroCampo = (v) => (v === null || v === undefined || Number(v) === 0 ? '' : numeroBR(v, { casas: 2 }));

// Campos que continuam abertos numa carga já acertada (não entram na conta do acerto).
const LIVRES_NO_ACERTO = [
  'cliente_id', 'origem_cidade', 'origem_uf', 'destino_cidade', 'destino_uf', 'adto_valor', 'adto_recebido_em', 'agenciamento_pago_em',
  'mercadoria', 'peso_kg', 'data_descarga', 'desc_outros_descricao', 'comprovante_status', 'comprovante_postado_em', 'comprovante_rastreio',
  'saldo_recebido_em', 'cte_numero', 'km_estimado', 'observacao', 'complemento_de',
];
// E os que ficam como estão (o app manda o valor que já estava).
const TRAVADOS_NO_ACERTO = [
  'data_carregamento', 'valor_frete', 'pedagio_modo', 'pedagio_valor', 'estadia_valor', 'desc_inss', 'desc_sest_senat', 'desc_irrf',
  'desc_outros', 'comissao_pct',
];
const DO_AGENCIAMENTO = ['agenciado_por', 'agenciador_id', 'agenciamento_valor'];
const opcoesUf = [['', 'UF'], ...UFS.map((u) => [u, u])];

export function telaCarga({ params }) {
  const tela = nestaTela();
  const nova = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, nova ? 'Nova carga' : 'Carga');
  const pagina = casca({ titulo, voltar: '/viagens', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
  const linkVoltar = pagina.querySelector('.topo a[aria-label="Voltar"]');

  (async () => {
    const f = nova ? null : await fretes.obter(params.id);
    if (!nova && !f) {
      trocar(corpo, vazio({ titulo: 'Carga não encontrada', texto: 'Ela pode ter sido excluída.', acao: h('a', { class: 'botao', href: '#/viagens' }, 'Ver viagens') }));
      return;
    }
    const viagemId = f?.viagem_id ?? params.viagem;
    const [v, listaClientes, pessoas, todasRegras, frota, cargasDaViagem, paradasAntes] = await Promise.all([
      viagens.obter(viagemId), clientes.listar(), colaboradores.listar(), regras.listar(), veiculos.listar(),
      fretes.daViagem(viagemId), nova ? [] : entregas.daCarga(f.id),
    ]);
    if (!v) {
      trocar(corpo, vazio({ titulo: 'Viagem não encontrada', acao: h('a', { class: 'botao', href: '#/viagens' }, 'Ver viagens') }));
      return;
    }
    const voltar = `/viagens/${v.id}`;
    linkVoltar?.setAttribute('href', `#${voltar}`);
    const caminhao = frota.find((x) => x.id === v.veiculo_id);
    const hoje = hojeDaEmpresa();
    // Carga num acerto fechado do motorista: valores e datas não mudam; o
    // recebimento, o comprovante e os dados da entrega, sim. Se o agenciamento
    // já foi acertado, quem agenciou e o valor também ficam.
    // A carga importada dos programas antigos fica como acertada (foi acertada lá).
    const travada = Boolean(f) && Boolean(f.acerto_id || v.status === 'acertada');
    const acertoDaCarga = travada && (f.acerto_id ?? v.acerto_id) ? await acertos.obter(f.acerto_id ?? v.acerto_id) : null;
    const agenciamentoAcertado = Boolean(f) && (await fretes.agenciamentoAcertado(f.id));
    const livres = new Set([...LIVRES_NO_ACERTO, ...(agenciamentoAcertado ? [] : ['agenciou', 'agenciamento_valor'])]);
    const gravavel = podeGravar() && (Boolean(f) || v.status !== 'acertada');

    const clientesAtivos = listaClientes.filter((c) => c.ativo || c.id === f?.cliente_id);
    const ativos = pessoas.filter((p) => p.ativo || p.id === f?.agenciador_id);
    const diaDaCarga = () => form.querySelector('[name="data_carregamento"]')?.value || hoje;
    const regraDe = (colaboradorId) => regraDeAgenciamento(todasRegras, colaboradorId, diaDaCarga(), v.veiculo_id);

    // Quem agenciou: primeiro quem tem regra de agenciamento, depois a
    // transportadora, depois os outros colaboradores.
    const comRegra = ativos.filter((p) => regraDeAgenciamento(todasRegras, p.id, f?.data_carregamento ?? hoje, v.veiculo_id));
    const semRegra = ativos.filter((p) => !comRegra.includes(p));
    const nome = (p) => (p.apelido ? `${p.nome} (${p.apelido})` : p.nome);
    const opcoesAgenciou = [
      ['', 'Ninguém'],
      ...comRegra.map((p) => [`c:${p.id}`, nome(p)]),
      ['transportadora', 'A transportadora (agenciamento dela)'],
      ...semRegra.map((p) => [`c:${p.id}`, `${nome(p)} · sem regra de agenciamento`]),
    ];
    const agenciouInicial = f?.agenciado_por === 'transportadora' ? 'transportadora' : f?.agenciador_id ? `c:${f.agenciador_id}` : '';

    // Data da carga: dentro da viagem.
    const dataPadrao = f?.data_carregamento
      ?? (hoje < v.data_saida ? v.data_saida : v.data_chegada && hoje > v.data_chegada ? v.data_chegada : hoje);

    const temDetalhe = f && (f.mercadoria || f.peso_kg || f.data_descarga || Number(f.estadia_valor) || Number(f.desc_inss)
      || Number(f.desc_sest_senat) || Number(f.desc_irrf) || Number(f.desc_outros) || f.comprovante_status !== 'nao_precisa'
      || f.saldo_recebido_em || f.cte_numero || f.km_estimado || f.comissao_pct != null || f.observacao);

    const blocoPedagio = campo({
      rotulo: 'Valor do pedágio embutido (R$)', nome: 'pedagio_valor', formato: 'dinheiro', valor: dinheiroCampo(f?.pedagio_valor),
      dica: 'Parte do frete que é do pedágio: sai do líquido e é o que separar para a fatura da tag.', atributos: { inputmode: 'decimal' },
    });
    const blocoValorAgenciamento = campo({
      rotulo: 'Valor do agenciamento (R$)', nome: 'agenciamento_valor', formato: 'dinheiro', valor: dinheiroCampo(f?.agenciamento_valor),
      atributos: { inputmode: 'decimal' },
    });
    const dicaAgenciamento = h('p', { class: 'dica' });
    blocoValorAgenciamento.append(dicaAgenciamento);
    const blocoPagoTransportadora = campo({
      rotulo: 'Pago à transportadora em', nome: 'agenciamento_pago_em', tipo: 'date', valor: f?.agenciamento_pago_em ?? '',
      dica: 'Deixe em branco enquanto não pagar: fica como conta a pagar. Não entra no acerto de ninguém.',
    });
    const blocoPostado = h('div', { class: 'grade-2' },
      campo({ rotulo: 'Postado em', nome: 'comprovante_postado_em', tipo: 'date', valor: f?.comprovante_postado_em ?? '' }),
      campo({ rotulo: 'Rastreio', nome: 'comprovante_rastreio', valor: f?.comprovante_rastreio ?? '', maiusculas: true }));
    const contas = h('dl', { class: 'linha-dados resumo', 'aria-live': 'polite' });

    let agenciamentoMexido = !nova && Number(f?.agenciamento_valor) > 0;

    // Complemento: carga menor pega na mesma rota. A que já tem complemento
    // não vira complemento de outra.
    const principais = principaisDaViagem(cargasDaViagem, { menos: f?.id });
    const seusComplementos = f ? cargasDaViagem.filter((x) => x.complemento_de === f.id) : [];
    const descricaoDaCarga = (x) => [dinheiro(x.valor_frete), textoDaRota(x) || null, data(x.data_carregamento)].filter(Boolean).join(' · ');
    const campoComplemento = principais.length > 0 && seusComplementos.length === 0 && campo({
      rotulo: 'Esta carga é',
      nome: 'complemento_de',
      tipo: 'select',
      valor: f?.complemento_de ?? '',
      opcoes: [['', 'A carga principal (ou a única da viagem)'], ...principais.map((x) => [x.id, `Complemento de ${descricaoDaCarga(x)}`])],
      dica: 'Complemento: carga menor pega na mesma rota para aproveitar a viagem. Tem contratante, valor e CT-e próprios.',
    });
    const paradas = editorDeParadas(paradasAntes);

    const form = formulario({
      filhos: [
        campo({
          rotulo: 'Contratante (transportadora ou cliente)',
          nome: 'cliente_id',
          tipo: 'select',
          valor: f?.cliente_id ?? '',
          opcoes: [['', clientesAtivos.length ? 'Escolha o contratante' : 'Nenhum cliente cadastrado'], ...clientesAtivos.map((c) => [c.id, c.nome])],
          dica: 'Não está na lista? Cadastre em Mais → Clientes e volte aqui.',
        }),
        campoComplemento,
        campo({
          rotulo: 'Data do carregamento', nome: 'data_carregamento', tipo: 'date', obrigatorio: true, valor: dataPadrao,
          atributos: { min: v.data_saida, max: v.data_chegada ?? null },
          aoMudar: () => atualizar(),
        }),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Origem (cidade)', nome: 'origem_cidade', formato: 'nome', valor: f?.origem_cidade ?? '' }),
          campo({ rotulo: 'UF', nome: 'origem_uf', tipo: 'select', valor: f?.origem_uf ?? '', opcoes: opcoesUf })),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Destino (cidade)', nome: 'destino_cidade', formato: 'nome', valor: f?.destino_cidade ?? '' }),
          campo({ rotulo: 'UF', nome: 'destino_uf', tipo: 'select', valor: f?.destino_uf ?? '', opcoes: opcoesUf })),
        paradas.elemento,
        campo({
          rotulo: 'Valor do frete (R$)', nome: 'valor_frete', obrigatorio: true, formato: 'dinheiro', valor: dinheiroCampo(f?.valor_frete),
          dica: 'O frete bruto, como está no contrato.', atributos: { inputmode: 'decimal' }, aoMudar: () => atualizar(),
        }),
        campo({
          rotulo: 'Pedágio',
          nome: 'pedagio_modo',
          tipo: 'segmentos',
          valor: f?.pedagio_modo ?? 'sem',
          opcoes: [['sem', 'Sem pedágio'], ['contratante_tag', 'Na tag', 'o contratante paga'], ['embutido', 'Embutido', 'no valor do frete']],
          aoMudar: () => atualizar(),
        }),
        blocoPedagio,
        h('div', { class: 'grade-2' },
          campo({
            rotulo: 'Adiantamento (R$)', nome: 'adto_valor', formato: 'dinheiro', valor: dinheiroCampo(f?.adto_valor),
            dica: 'O que o contratante paga no carregamento.', atributos: { inputmode: 'decimal' }, aoMudar: () => atualizar(),
          }),
          campo({ rotulo: 'Adiantamento recebido em', nome: 'adto_recebido_em', tipo: 'date', valor: f?.adto_recebido_em ?? '' })),
        campo({
          rotulo: 'Quem agenciou esta carga?',
          nome: 'agenciou',
          tipo: 'select',
          valor: agenciouInicial,
          opcoes: opcoesAgenciou,
          aoMudar: () => {
            agenciamentoMexido = false;
            atualizar();
          },
        }),
        blocoValorAgenciamento,
        blocoPagoTransportadora,
        h('details', { class: 'mais-detalhes', open: Boolean(temDetalhe) },
          h('summary', null, 'Mais detalhes (descontos, estadia, comprovante…)'),
          h('div', { class: 'secao' },
            h('div', { class: 'grade-2' },
              campo({ rotulo: 'Mercadoria', nome: 'mercadoria', valor: f?.mercadoria ?? '' }),
              campo({ rotulo: 'Peso (kg)', nome: 'peso_kg', formato: 'km', valor: f?.peso_kg != null ? numeroBR(Math.round(f.peso_kg)) : '', atributos: { inputmode: 'numeric' } })),
            campo({
              rotulo: 'Data da descarga', nome: 'data_descarga', tipo: 'date', valor: f?.data_descarga ?? '',
              dica: 'O prazo do saldo conta a partir daqui.',
            }),
            campo({
              rotulo: 'Estadia cobrada (R$)', nome: 'estadia_valor', formato: 'dinheiro', valor: dinheiroCampo(f?.estadia_valor),
              dica: 'Quando o contratante paga pela espera na carga ou na descarga.', atributos: { inputmode: 'decimal' }, aoMudar: () => atualizar(),
            }),
            h('p', { class: 'pequeno' }, 'Descontos do contratante (como vêm no contrato):'),
            h('div', { class: 'grade-2' },
              campo({ rotulo: 'INSS (R$)', nome: 'desc_inss', formato: 'dinheiro', valor: dinheiroCampo(f?.desc_inss), atributos: { inputmode: 'decimal' }, aoMudar: () => atualizar() }),
              campo({ rotulo: 'SEST/SENAT (R$)', nome: 'desc_sest_senat', formato: 'dinheiro', valor: dinheiroCampo(f?.desc_sest_senat), atributos: { inputmode: 'decimal' }, aoMudar: () => atualizar() })),
            h('div', { class: 'grade-2' },
              campo({ rotulo: 'IR (R$)', nome: 'desc_irrf', formato: 'dinheiro', valor: dinheiroCampo(f?.desc_irrf), atributos: { inputmode: 'decimal' }, aoMudar: () => atualizar() }),
              campo({ rotulo: 'Outros (R$)', nome: 'desc_outros', formato: 'dinheiro', valor: dinheiroCampo(f?.desc_outros), atributos: { inputmode: 'decimal' }, aoMudar: () => atualizar() })),
            campo({ rotulo: 'O que são os outros descontos?', nome: 'desc_outros_descricao', valor: f?.desc_outros_descricao ?? '' }),
            campo({
              rotulo: 'Comprovante de entrega',
              nome: 'comprovante_status',
              tipo: 'segmentos',
              valor: f?.comprovante_status ?? 'nao_precisa',
              opcoes: [['nao_precisa', 'Não precisa'], ['a_postar', 'A postar'], ['postado', 'Postado'], ['entregue', 'Entregue']],
              aoMudar: () => atualizar(),
            }),
            blocoPostado,
            campo({ rotulo: 'Saldo recebido em', nome: 'saldo_recebido_em', tipo: 'date', valor: f?.saldo_recebido_em ?? '' }),
            h('div', { class: 'grade-2' },
              campo({ rotulo: 'Nº do CT-e / Manifesto', nome: 'cte_numero', valor: f?.cte_numero ?? '', atributos: { inputmode: 'numeric' } }),
              campo({ rotulo: 'Km da carga (estimado)', nome: 'km_estimado', formato: 'km', valor: f?.km_estimado != null ? numeroBR(f.km_estimado) : '', atributos: { inputmode: 'numeric' } })),
            campo({
              rotulo: 'Comissão diferente só nesta carga (%)', nome: 'comissao_pct', formato: 'percentual',
              valor: f?.comissao_pct != null ? String(f.comissao_pct).replace('.', ',') : '',
              dica: 'Deixe em branco para usar a comissão combinada com o motorista.', atributos: { inputmode: 'decimal' },
            }),
            campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: f?.observacao ?? '' }))),
        secao('Contas da carga', contas),
      ],
      textoBotao: nova ? 'Salvar carga' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (lidos) => {
        const d = completar(lidos);
        const erros = {};
        const n = numeros(d);
        if (!d.data_carregamento) erros.data_carregamento = 'Informe a data do carregamento.';
        else if (d.data_carregamento < v.data_saida || (v.data_chegada && d.data_carregamento > v.data_chegada)) {
          erros.data_carregamento = `A carga precisa ser da viagem: de ${data(v.data_saida)}${v.data_chegada ? ` a ${data(v.data_chegada)}` : ' em diante'}.`;
        }
        if (!(n.valor_frete > 0)) erros.valor_frete = 'Informe o valor do frete, como 12.500,00.';
        for (const campoValor of ['pedagio_valor', 'adto_valor', 'agenciamento_valor', 'estadia_valor', 'desc_inss', 'desc_sest_senat', 'desc_irrf', 'desc_outros']) {
          if (Number.isNaN(n[campoValor]) || n[campoValor] < 0) erros[campoValor] = 'Valor inválido.';
        }
        if (d.pedagio_modo === 'embutido' && !(n.pedagio_valor > 0)) erros.pedagio_valor = 'Informe o valor do pedágio embutido.';
        const descontos = n.pedagio_valor + n.desc_inss + n.desc_sest_senat + n.desc_irrf + n.desc_outros;
        if (n.valor_frete > 0 && descontos > n.valor_frete) erros.valor_frete = 'Pedágio e descontos passam do valor do frete.';
        if (n.valor_frete > 0 && n.adto_valor > liquidoDoFrete(n) + n.estadia_valor) erros.adto_valor = 'O adiantamento passa do frete líquido.';
        if (d.data_descarga && d.data_carregamento && d.data_descarga < d.data_carregamento) erros.data_descarga = 'A descarga não pode ser antes do carregamento.';
        if (d.comprovante_status === 'postado' && !d.comprovante_postado_em) erros.comprovante_postado_em = 'Informe quando foi postado.';
        if (d.peso_kg && !(lerInteiro(d.peso_kg) >= 0)) erros.peso_kg = 'Só números.';
        if (d.km_estimado && !(lerInteiro(d.km_estimado) >= 0)) erros.km_estimado = 'Só números.';
        if (d.comissao_pct) {
          const p = lerNumero(d.comissao_pct);
          if (!(p >= 0 && p <= 100)) erros.comissao_pct = 'Entre 0 e 100.';
        }
        return { ...erros, ...paradas.validar(d.data_carregamento) };
      },
      aoEnviar: async (lidos) => {
        const d = completar(lidos);
        const n = numeros(d);
        const agenciou = d.agenciou;
        const porColaborador = agenciou.startsWith('c:');
        const dados = {
          cliente_id: d.cliente_id || null,
          data_carregamento: d.data_carregamento,
          origem_cidade: d.origem_cidade || null,
          origem_uf: d.origem_uf || null,
          destino_cidade: d.destino_cidade || null,
          destino_uf: d.destino_uf || null,
          valor_frete: n.valor_frete,
          pedagio_modo: d.pedagio_modo || 'sem',
          pedagio_valor: d.pedagio_modo === 'embutido' ? n.pedagio_valor : 0,
          adto_valor: n.adto_valor,
          adto_recebido_em: d.adto_recebido_em || null,
          agenciado_por: porColaborador ? 'colaborador' : agenciou === 'transportadora' ? 'transportadora' : null,
          agenciador_id: porColaborador ? agenciou.slice(2) : null,
          agenciamento_valor: agenciou ? n.agenciamento_valor : 0,
          agenciamento_pago_em: agenciou === 'transportadora' ? d.agenciamento_pago_em || null : null,
          mercadoria: d.mercadoria || null,
          peso_kg: d.peso_kg ? lerInteiro(d.peso_kg) : null,
          data_descarga: d.data_descarga || null,
          estadia_valor: n.estadia_valor,
          desc_inss: n.desc_inss,
          desc_sest_senat: n.desc_sest_senat,
          desc_irrf: n.desc_irrf,
          desc_outros: n.desc_outros,
          desc_outros_descricao: d.desc_outros_descricao || null,
          comprovante_status: d.comprovante_status || 'nao_precisa',
          comprovante_postado_em: ['postado', 'entregue'].includes(d.comprovante_status) ? d.comprovante_postado_em || null : null,
          comprovante_rastreio: ['postado', 'entregue'].includes(d.comprovante_status) ? d.comprovante_rastreio || null : null,
          saldo_recebido_em: d.saldo_recebido_em || null,
          cte_numero: d.cte_numero || null,
          km_estimado: d.km_estimado ? lerInteiro(d.km_estimado) : null,
          comissao_pct: d.comissao_pct ? lerNumero(d.comissao_pct) : null,
          observacao: d.observacao || null,
        };
        if (campoComplemento) dados.complemento_de = d.complemento_de || null;
        // A hora do "Carga entregue" vale enquanto o dia da descarga não muda.
        if (f?.entregue_em && dados.data_descarga !== f.data_descarga) dados.entregue_em = null;
        // Já acertada: o que entra na conta do acerto fica como estava.
        if (travada) for (const campoTravado of TRAVADOS_NO_ACERTO) dados[campoTravado] = f[campoTravado];
        if (agenciamentoAcertado) for (const campoTravado of DO_AGENCIAMENTO) dados[campoTravado] = f[campoTravado];
        const salva = nova ? await fretes.criar({ ...dados, viagem_id: v.id }) : await fretes.atualizar(f.id, dados, f);
        await entregas.salvarDaCarga(salva.id, paradas.ler(), paradasAntes);
        avisar(nova ? 'Carga salva.' : 'Alterações salvas.');
        tela.ir(voltar, { substituir: true });
      },
      extras: !nova && gravavel && !travada && !agenciamentoAcertado && h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          const ok = await confirmar({ titulo: 'Excluir esta carga?', texto: 'Use só para carga lançada por engano. Ela vai para a lixeira.', sim: 'Excluir', perigo: true });
          if (!ok) return;
          try {
            await fretes.excluir(f.id);
            avisar('Carga excluída.');
            tela.ir(voltar, { substituir: true });
          } catch (erro) {
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Excluir carga lançada por engano'),
    });
    // O número pelo código de barras do DACTE (0.17.0); o CNPJ de quem emitiu,
    // se for de um cliente cadastrado, escolhe o contratante (se estiver em branco).
    const campoCte = form.querySelector('[name="cte_numero"]');
    if (campoCte && gravavel) {
      campoCte.closest('.grade-2').after(leitorDaChave({
        campoNumero: campoCte,
        nomeDoEmitente: (cnpj) => listaClientes.find((c) => c.documento === cnpj)?.nome ?? null,
        aoLer: (c) => {
          const emitente = clientesAtivos.find((x) => x.documento === c.cnpj);
          const escolha = form.querySelector('[name="cliente_id"]');
          if (emitente && escolha && !escolha.value) {
            escolha.value = emitente.id;
            escolha.dispatchEvent(new Event('change', { bubbles: true }));
          }
        },
      }));
    }

    // Números do formulário (vazio = 0; texto inválido = NaN)
    function numeros(d) {
      const ler = (nomeCampo) => (d[nomeCampo] ? lerNumero(d[nomeCampo]) : 0);
      return {
        valor_frete: ler('valor_frete'),
        pedagio_valor: d.pedagio_modo === 'embutido' ? ler('pedagio_valor') : 0,
        adto_valor: ler('adto_valor'),
        agenciamento_valor: ler('agenciamento_valor'),
        estadia_valor: ler('estadia_valor'),
        desc_inss: ler('desc_inss'),
        desc_sest_senat: ler('desc_sest_senat'),
        desc_irrf: ler('desc_irrf'),
        desc_outros: ler('desc_outros'),
      };
    }

    // Os dados do formulário com os campos travados (desativados), que o
    // formulário não lê.
    function completar(lidos) {
      return travada || agenciamentoAcertado ? { ...lerFormulario(), ...lidos } : lidos;
    }

    function lerFormulario() {
      const d = {};
      for (const el of form.elements) {
        if (!el.name) continue;
        if (el.type === 'radio') {
          if (el.checked) d[el.name] = el.value;
        } else d[el.name] = el.value;
      }
      return d;
    }

    function atualizar() {
      const d = lerFormulario();
      const n = numeros(d);
      const valido = (x) => (Number.isFinite(x) ? x : 0);
      const conta = Object.fromEntries(Object.entries(n).map(([k, x]) => [k, valido(x)]));
      blocoPedagio.hidden = d.pedagio_modo !== 'embutido';
      blocoPostado.hidden = !['postado', 'entregue'].includes(d.comprovante_status);

      const agenciou = d.agenciou ?? '';
      blocoValorAgenciamento.hidden = !agenciou;
      blocoPagoTransportadora.hidden = agenciou !== 'transportadora';
      dicaAgenciamento.textContent = '';
      if (agenciou.startsWith('c:')) {
        const regra = regraDe(agenciou.slice(2));
        const sugerido = agenciamentoSugerido(regra, conta);
        if (regra) {
          const faixaTexto = regra.faixa_ate != null
            ? ` (${dinheiro(regra.valor)} até ${dinheiro(regra.faixa_ate)} de frete ${regra.base === 'liquido' ? 'líquido' : 'bruto'}; ${dinheiro(regra.valor_acima)} acima)`
            : '';
          dicaAgenciamento.textContent = `Pela regra: ${dinheiro(sugerido)}${faixaTexto}. Dá para trocar nesta carga.`;
          if (!agenciamentoMexido) form.querySelector('[name="agenciamento_valor"]').value = numeroBR(sugerido, { casas: 2 });
        } else {
          dicaAgenciamento.textContent = 'Sem regra de agenciamento para esta pessoa: digite o valor.';
        }
      } else if (agenciou === 'transportadora') {
        dicaAgenciamento.textContent = 'O valor que a transportadora cobra por ter oferecido a carga.';
      }

      const liquido = liquidoDoFrete(conta);
      const saldo = saldoDoFrete(conta);
      trocar(contas,
        h('dt', null, 'Frete líquido'), h('dd', null, dinheiro(liquido)),
        h('dt', null, 'Saldo a receber'), h('dd', null, dinheiro(saldo)),
        agenciou === 'transportadora' && conta.agenciamento_valor > 0 && [h('dt', null, 'A pagar à transportadora'), h('dd', null, dinheiro(conta.agenciamento_valor))]);
    }

    form.querySelector('[name="agenciamento_valor"]').addEventListener('input', () => {
      agenciamentoMexido = true;
    });

    // Já acertada: trava o que não está livre (o formulário lê os travados
    // pelo `completar`, com o valor que já estava).
    if (travada || agenciamentoAcertado) {
      for (const el of form.elements) {
        if (!el.name || el.type === 'submit') continue;
        if (travada ? !livres.has(el.name) : DO_AGENCIAMENTO.includes(el.name) || el.name === 'agenciou') el.disabled = true;
      }
    }

    trocar(corpo,
      travada && faixa(
        `${v.importacao_id && !f.acerto_id ? 'Esta carga veio dos programas antigos e já foi acertada neles'
          : `Esta carga já entrou no acerto${acertoDaCarga ? ` nº ${acertoDaCarga.numero}/${acertoDaCarga.ano}` : ''} do motorista`}: valores e datas não mudam mais. `
        + 'Dá para registrar o recebimento, o comprovante e os dados da entrega.', 'info'),
      !travada && agenciamentoAcertado && faixa('O agenciamento desta carga já foi acertado: quem agenciou e o valor não mudam mais.', 'info'),
      f && textoDosCamposDoMotorista(f) && faixa(`O motorista preencheu pelo app: ${textoDosCamposDoMotorista(f)}. Confira e corrija se precisar.`, 'info'),
      h('p', { class: 'pequeno' }, `Viagem de ${data(v.data_saida)}${caminhao ? ` · ${caminhao.placa}` : ''}.`),
      f && blocoDaEntrega(f, { hoje, gravavel, tela, pendentes: paradasAntes.filter((x) => !x.data_entrega).length }),
      seusComplementos.length > 0 && h('p', { class: 'pequeno' },
        `Complemento desta carga: ${seusComplementos.map(descricaoDaCarga).join('; ')}.`),
      form);
    atualizar();
    if (nova) form.querySelector('[name="cliente_id"]')?.focus();
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return pagina;
}

// "Carga entregue": o dia e a hora da entrega no destino final (as paradas
// que faltavam também ficam entregues). Depois de entregue, só a informação.
function blocoDaEntrega(f, { hoje, gravavel, tela, pendentes }) {
  const destino = [f.destino_cidade, f.destino_uf].filter(Boolean).join('/');
  if (f.data_descarga) {
    return faixa(`Entregue${destino ? ` em ${destino}` : ''} ${f.entregue_em
      ? `em ${textoDoMomento(f.entregue_em, { fuso: sessao.empresa?.fuso_horario })}` : `no dia ${data(f.data_descarga)}`}.`, 'ok');
  }
  if (!gravavel || f.data_carregamento > hoje) return null;
  const botao = h('button', { class: 'botao cheio', type: 'button' }, 'Carga entregue');
  botao.addEventListener('click', async () => {
    const ok = await confirmar({
      titulo: 'Carga entregue?',
      texto: `Marca a entrega${destino ? ` em ${destino}` : ''} hoje, agora.${pendentes > 0 ? ' As entregas no caminho que faltavam também ficam entregues.' : ''} Dá para corrigir a data depois, em "Mais detalhes".`,
      sim: 'Carga entregue',
    });
    if (!ok) return;
    botao.disabled = true;
    try {
      await cargaEntregue(f, { hoje });
      avisar('Carga entregue.');
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
      botao.disabled = false;
    }
  });
  return h('div', { class: 'acoes' }, botao);
}
